import { TextLink, EmailVerificationStatusIcon } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import {
  Contact,
  OrderedContact,
  SelectedContact,
} from '@mm/state/interfaces/contacts';
import { setContactsOrderBy } from '@mm/state/modules/contactTableState/actions';
import { ContactsOrderBy } from '@mm/state/modules/contactTableState/interfaces';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { getTwilioAvailable } from '@mm/state/selectors/viewSelectors';
import { Pagination, StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import contactsImg from './empty-contacts-state.svg';
import trashIcon from 'assets/icons/trash.svg';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { Dispatch } from 'redux';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../common/components/TableGrid/TableGrid';
import { randomId } from '../../../../../common/helpers/utils';
import EmptyPageBacksplash from '../../../../common/EmptyPageBacksplash/EmptyPageBacksplash';
import { ChannelIcon } from '../../../liveChat/components/ChannelIcon';
import { ContactDeleteModal } from '../ContactDeleteModal/ContactDeleteModal';
import addSvg from './add.svg';
import './ContactTable.scss';
import { ContactTagsPopup } from './ContactTagsPopup';
import { MoreOutlined } from '@ant-design/icons';
import { remappedNames } from '../../../beta/modules/Contacts/MyLeadsFiltersPanel/helpers';

type Entity = {
  id: number | string;
  [key: string]: any;
};

export type ComponentDataItem<T = { [key: string]: any }> = {
  label: string | ReactNode;
  onChange?: (...params: any[]) => any;
  keyName?: keyof T;
  Cell?: (props: { item: Entity; orderNumber?: number }) => JSX.Element;
  fr?: number;
  rowClassName?: string;
  className?: string;
};

type ExtendedOrderedContact = OrderedContact & { rowClassName: string };

type OuterProps = {
  searchBlock?: JSX.Element;
  searchActive?: boolean;
  onChangePagination: (pagingNumber: number) => void;
  getSelectedContact: (contactId: number) => void;
  pagination?: Pagination;
  contacts: OrderedContact[];
  deleteSelectedContact: (
    contactId: number,
    deleteRemoteMessages: boolean,
  ) => Promise<void>;
  selectedContact?: SelectedContact | null;
  disableSorting?: boolean;
  hideTotal?: boolean;
  toggleSortOrder?: () => void;
  css?: {
    wrapper?: string;
    container?: string;
    rowsContainer?: string;
  };
};

type StateAndHandlers = {
  items: OrderedContact[];
  setItems: (items: OrderedContact[]) => void;
};

type Handlers = {
  onDeleteClick: (e: React.SyntheticEvent<HTMLDivElement>, id: number) => void;
  onRowClick: (id: number) => void;
  setSortOrder: (contactsOrderBy: ContactsOrderBy) => void;
};

type InnerProps = ReturnType<typeof mapDispatchToProps> &
  StateAndHandlers &
  Handlers;

type Props = OuterProps & InnerProps;

type DefultCellProps = {
  keyName: string;
  item: Entity;
};

export const DefaultCell = ({ keyName, item }: DefultCellProps) => (
  <GridCell key={keyName}>{item && item[keyName]}</GridCell>
);

type CellProps = { item: Contact & { fullName: string } };

export const renderDataCell =
  (field: string) =>
  ({ item }: CellProps) =>
    <GridCell className="contacts-table__grid-cell">{item[field]}</GridCell>;

export const renderOptionalNumberCell =
  (field: string, optionField: string) =>
  ({ item }: CellProps) =>
    (
      <GridCell className="contacts-table__grid-cell">
        {item[optionField] ? item[field] : ''}
      </GridCell>
    );

export const renderFullNameCell = ({ item }: CellProps) => {
  const generateName = (contact: Contact) => {
    if (contact.firstName && contact.lastName && contact.username) {
      if (
        contact.firstName === 'Instagram Contact' &&
        contact.lastName === 'IG' &&
        contact.username
      ) {
        return `@${contact.username}`;
      }
      return `${contact.firstName} ${contact.lastName} (@${contact.username})`;
    }
    if (contact.firstName && contact.lastName) {
      return `${contact.firstName} ${contact.lastName}`;
    }
    if (contact.firstName) {
      return `${contact.firstName}`;
    }
    if (contact.lastName) {
      return `${contact.lastName}`;
    }
    if (contact.username) {
      return `@${contact.username}`;
    }
  };

  return (
    <GridCell>
      <div>{generateName(item)}</div>
    </GridCell>
  );
};

export const renderPlatformCell = ({ item }: CellProps) => {
  const { remoteId } = item;
  return (
    <GridCell>
      <ChannelIcon remoteUserId={remoteId} />
    </GridCell>
  );
};

export const renderSourceCell =
  (field: string) =>
  ({ item }: CellProps) =>
    (
      <GridCell className="contacts-table__grid-cell">
        {remappedNames(item[field]).friendlyName}
      </GridCell>
    );

export const renderEmailCell = ({ item }: CellProps) => {
  const { email } = item;

  return (
    <GridCell className="contacts-grid__email-cell">
      <EmailVerificationStatusIcon
        contact={item}
        className="contacts-grid__email-cell-icon"
      />
      <div title={email || ''} className="contacts-grid__email-cell-email">
        {email}
      </div>
    </GridCell>
  );
};

const renderActionsCell =
  (setContactForDeletion: (contact: Contact) => void) =>
  ({ item }: CellProps) => {
    // eslint doesn't realize that this is a function component.
    /* eslint-disable react-hooks/rules-of-hooks */
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isTagsOpen, setIsTagsOpen] = useState(false);
    /* eslint-enable react-hooks/rules-of-hooks */

    const stopPropagation = (e: React.SyntheticEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    const onDropdownToggle = (e: React.SyntheticEvent<HTMLDivElement>) => {
      setDropdownOpen(!dropdownOpen);
      setIsTagsOpen(false);
      stopPropagation(e);
    };

    const onOpenTagPopupClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
      setIsTagsOpen(!isTagsOpen);
    };

    const onContactRemove = (e: React.SyntheticEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setContactForDeletion(item);
      onDropdownToggle(e);
    };

    return (
      <GridCell overflowable className="action-cell">
        <Dropdown isOpen={dropdownOpen} toggle={onDropdownToggle}>
          <DropdownToggle
            tag="span"
            data-toggle="dropdown"
            aria-expanded={dropdownOpen}
            onClick={onDropdownToggle}
            className="contacts-grid__dropdown-cell"
          >
            <MoreOutlined alt="menu ellipsis" />
          </DropdownToggle>
          <DropdownMenu
            right
            className="contacts-grid__dropdown-sidemenu__wr"
            onClick={stopPropagation}
          >
            <div className="contacts-grid__dropdown-sidemenu">
              <div
                className="contacts-grid__dropdown-item"
                onClick={onOpenTagPopupClick}
              >
                <div className="contacts-grid__dropdown-action">
                  <img
                    src={addSvg}
                    className="contacts-grid__icon"
                    alt="delete contact"
                  />
                  Add Tag
                </div>
              </div>
              <div
                className="contacts-grid__dropdown-item"
                onClick={onContactRemove}
              >
                <div className="contacts-grid__dropdown-action">
                  <img
                    src={trashIcon}
                    className="contacts-grid__icon"
                    alt="delete contact"
                  />
                  Delete
                </div>
              </div>
            </div>
            {isTagsOpen && (
              <div className="contacts-grid__dropdown-sidemenu">
                <ContactTagsPopup tags={item.tags} contactId={item.id} />
              </div>
            )}
          </DropdownMenu>
        </Dropdown>
      </GridCell>
    );
  };

const setSortOrder =
  ({ setContactsOrderBy, onChangePagination }: Props) =>
  (contactsOrderBy: ContactsOrderBy) => {
    setContactsOrderBy(contactsOrderBy);
    onChangePagination(1);
  };

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  setContactsOrderBy: (contactsOrderBy: ContactsOrderBy) =>
    dispatch(setContactsOrderBy(contactsOrderBy)),
});

const onRowClick =
  ({ getSelectedContact }: Props) =>
  (id: number) => {
    getSelectedContact(id);
  };

function componentDidUpdate(this: LifeCycleThis<Props>, prevProps: Props) {
  if (prevProps.contacts !== this.props.contacts) {
    this.props.setItems(this.props.contacts);
  }
}

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
  withState('items', 'setItems', []),
  withProps(({ selectedContact, items }) => {
    if (!selectedContact) {
      return {};
    }
    return {
      items: items.map((item: Contact) => ({
        ...item,
        fullName: `${item.firstName} ${item.lastName}`,
        rowClassName:
          selectedContact.id === item.id ? 'grid-row--selected' : '',
      })),
    };
  }),
  withHandlers({ onRowClick }),
  withHandlers({ setSortOrder }),
  lifecycle<Props, {}>({ componentDidUpdate }),
);

const ContactsTable = (props: Props) => {
  const {
    onChangePagination,
    items,
    pagination,
    deleteSelectedContact,
    searchActive,
    onRowClick,
    disableSorting,
    hideTotal,
    css,
    toggleSortOrder,
  } = props;

  const twilioFeatFlag = useSelector(companyFeatureFlagsSelector).twilio;
  const twilioAvailableFlag = useSelector(getTwilioAvailable);
  const isSmsSupported = twilioFeatFlag || twilioAvailableFlag;
  const [contactForDeletion, setContactForDeletion] = useState<Contact>();

  const data: ComponentDataItem[] = [
    {
      label: 'Name',
      keyName: 'full_name',
      Cell: renderFullNameCell,
    },
    {
      label: 'Email',
      keyName: 'email',
      Cell: renderEmailCell,
    },
    ...(isSmsSupported
      ? [
          {
            label: 'Phone',

            keyName: 'phone',
            Cell: renderDataCell('phone'),
          },
        ]
      : []),
    {
      label: 'Source',
      keyName: 'source',
      Cell: renderSourceCell('source'),
    },

    {
      label: 'Date Created',
      keyName: 'created_at',
      Cell: renderDataCell('formattedCreatedAt'),
      onChange: () => toggleSortOrder?.(),
    },
    {
      label: '',
      Cell: renderActionsCell(setContactForDeletion),
    },
  ];

  // We could probably generalized next 15 lines into a custom hook,
  // but it is hard to come up with a good API right away, since there is
  // specifics to this component. If we seen more things needed like this,
  // we can generalize.
  const gridWrapper = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState<number | undefined>(undefined);

  const { screenHeight } = useResolutionInfo();

  useEffect(() => {
    if (gridWrapper.current !== null) {
      const renderedOffsetHeight = gridWrapper.current.offsetHeight as number;
      setHeight(renderedOffsetHeight - 200);
    }
  }, [screenHeight]);

  const rowStyle = height ? { height: `${height}px` } : {};

  if (!searchActive && !items?.length) {
    return (
      <EmptyPageBacksplash
        pageIcon={contactsImg}
        className="contacts-grid-empty-state"
      >
        No Contacts
      </EmptyPageBacksplash>
    );
  }
  const containerClassName = classNames('contacts-grid-wrapper', css?.wrapper);

  const matchIndex = data.findIndex(item => item.keyName === 'profilePic');
  if (matchIndex !== -1) {
    data[matchIndex].label = 'Avatar';
  }

  return (
    <div className={containerClassName} ref={gridWrapper}>
      <Grid className={classNames('contacts-grid', css?.container)}>
        <GridLabels
          className={classNames('contacts-grid__labels', {
            'contacts-grid__labels--with-sms': isSmsSupported,
          })}
          gridTemplateColumns={null} // Applied via CSS
        >
          {data.map(dataItem => (
            <TableLabel
              key={(dataItem.keyName as string) || dataItem.label?.toString()}
              dataItem={{
                ...dataItem,
                onChange: disableSorting ? undefined : dataItem.onChange,
              }}
              orderByAtTheEnd
            />
          ))}
        </GridLabels>

        <div
          className={classNames(
            'contacts-grid__rows-container',
            css?.rowsContainer,
            {
              'contacts-grid__rows-container--busy': searchActive,
            },
          )}
          style={rowStyle}
        >
          {searchActive && (
            <div className="contacts-grid__rows-container__busy-indicator">
              <div className="contacts-grid__rows-container__busy-indicator__content">
                <i className="fas fa-spinner fa-spin" />

                <span>Retrieving contacts...</span>
              </div>
            </div>
          )}
          {items.map((item: ExtendedOrderedContact) => (
            <GridRow
              key={item.id}
              onClick={() => (onRowClick ? onRowClick(item.id) : null)}
              className={classNames('contacts-grid__row', item.rowClassName, {
                'contacts-grid__row--with-sms': isSmsSupported,

                'contacts-grid__row-more-padding': items.length < 15,
              })}
              gridTemplateColumns={null} // Applied via CSS
            >
              {data.map(({ Cell, keyName }) =>
                Cell ? (
                  <Cell item={item} key={randomId()} />
                ) : (
                  <DefaultCell
                    key={item.id}
                    keyName={keyName as string}
                    item={item}
                  />
                ),
              )}
            </GridRow>
          ))}
        </div>
        <div className="contacts-grid__footer-container">
          {!!(items.length || pagination?.total) && !hideTotal && (
            <TextLink className="chat-starters__total">
              Found&nbsp;
              <span className="chat-starters__total-count">
                {pagination?.total || items.length}
              </span>
              &nbsp;contacts
            </TextLink>
          )}
        </div>
      </Grid>
      <ContactDeleteModal
        showEraseRemoteMessagesOption={
          !!contactForDeletion?.remoteId.startsWith('em_')
        }
        show={!!contactForDeletion}
        emailAddress={contactForDeletion?.email || ''}
        onDelete={async eraseRemoteMessages => {
          if (contactForDeletion) {
            await deleteSelectedContact(
              contactForDeletion.id,
              eraseRemoteMessages,
            );
          }
        }}
        onClose={() => setContactForDeletion(undefined)}
      />
    </div>
  );
};

export default enhance(ContactsTable);
